import { QueryKey as TanStackQueryKey } from "@tanstack/react-query";
import { BaseQueryKey } from "../base-query-key.service";
import {
    QueryKeyAfterPage,
    QueryKeyAfterLocale,
    QueryKeyAfterGroup,
    QueryKeyAfterScopes,
    QueryKeyAfterId,
    QueryKeyAfterArgs,
} from "./actors.type";

/**
 * Service class for generating query keys using the fluent builder pattern.
 *
 * This class extends BaseQueryKey to provide query key generation functionality.
 * It uses the fluent builder pattern to construct query keys for data fetching and caching.
 * The order is enforced: page > locale > group > scopes > id > args.
 * ! IMPORTANT: The values passed to the generator should be consistent to avoid breaking the query cache.
 *
 * @example
 * const generator = new QueryKeyGenerator();
 * const key = generator
 *   .page('/home')
 *   .locale('en')
 *   .group('user')
 *   .scopes(['profile', 'settings'])
 *   .id(123)
 *   .args({ filter: 'active' })
 *   .build();
 */
class QueryKeyGenerator extends BaseQueryKey<
    QueryKeyAfterPage,
    QueryKeyAfterLocale,
    QueryKeyAfterGroup,
    QueryKeyAfterScopes,
    QueryKeyAfterId,
    QueryKeyAfterArgs
> {
    /**
     * Private constructor to prevent external instantiation.
     */
    constructor() {
        super();
    }

    /**
     * Implementation of abstract method to create the fluent interface after page is set.
     *
     * @returns {QueryKeyAfterPage} - Returns interface for next available methods.
     */
    protected createAfterPageInterface(): QueryKeyAfterPage {
        return {
            locale: (locale: string) => this.locale(locale),
            group: (group: string) => this.group(group),
            scopes: (scopes: (string | number | symbol | undefined | null)[]) => this.scopes(scopes),
            id: (id: any) => this.id(id),
            args: (...args: any[]) => this.args(...args),
            build: () => this.build(),
        };
    }

    /**
     * Implementation of abstract method to create the fluent interface after locale is set.
     *
     * @returns {QueryKeyAfterLocale} - Returns interface for next available methods.
     */
    protected createAfterLocaleInterface(): QueryKeyAfterLocale {
        return {
            group: (group: string) => this.group(group),
            scopes: (scopes: (string | number | symbol | undefined | null)[]) => this.scopes(scopes),
            id: (id: any) => this.id(id),
            args: (...args: any[]) => this.args(...args),
            build: () => this.build(),
        };
    }

    /**
     * Implementation of abstract method to create the fluent interface after group is set.
     *
     * @returns {QueryKeyAfterGroup} - Returns interface for next available methods.
     */
    protected createAfterGroupInterface(): QueryKeyAfterGroup {
        return {
            scopes: (scopes: (string | number | symbol | undefined | null)[]) => this.scopes(scopes),
            id: (id: any) => this.id(id),
            args: (...args: any[]) => this.args(...args),
            build: () => this.build(),
        };
    }

    /**
     * Implementation of abstract method to create the fluent interface after scopes is set.
     *
     * @returns {QueryKeyAfterScopes} - Returns interface for next available methods.
     */
    protected createAfterScopesInterface(): QueryKeyAfterScopes {
        return {
            id: (id: any) => this.id(id),
            args: (...args: any[]) => this.args(...args),
            build: () => this.build(),
        };
    }

    /**
     * Implementation of abstract method to create the fluent interface after id is set.
     *
     * @returns {QueryKeyAfterId} - Returns interface for next available methods.
     */
    protected createAfterIdInterface(): QueryKeyAfterId {
        return {
            args: (...args: any[]) => this.args(...args),
            build: () => this.build(),
        };
    }

    /**
     * Implementation of abstract method to create the fluent interface after args is set.
     *
     * @returns {QueryKeyAfterArgs} - Returns interface for next available methods.
     */
    protected createAfterArgsInterface(): QueryKeyAfterArgs {
        return {
            build: () => this.build(),
        };
    }

    /**
     * Generate a query key for data fetching and caching.
     *
     * @returns {TanStackQueryKey} - An array representing the generated query key.
     *
     * @example
     * const generator = new QueryKeyGenerator();
     * const key = generator
     *   .page('/home')
     *   .locale('en')
     *   .group('user')
     *   .scopes(['profile', 'settings'])
     *   .id(123)
     *   .args({ filter: 'active' })
     *   .build();
     * // key will be:
     * [
     *   { page: '/home' },
     *   { language: 'en' },
     *   { group: 'user' },
     *   { scopes: ['profile', 'settings'] },
     *   { id: 123 },
     *   { optional: [{ filter: 'active' }] }
     * ]
     *
     * @example
     * // Skip optional methods
     * const key = generator
     *   .page('/home')
     *   .build();
     * // key will be:
     * [
     *   { page: '/home' },
     *   { language: 'en' }
     * ]
     */
    public build(): TanStackQueryKey {
        return this.builder();
    }
}

export { QueryKeyGenerator };
export default QueryKeyGenerator;
